import React from 'react';
import { motion } from 'framer-motion';
import ZodiacSignModal from './ZodiacSignModal';

const signoLabels = ['Signo Solar', 'Signo Lunar', 'Ascendente'];

const signoInfo = {
  'Áries': { elemento: 'Fogo', regente: 'Marte', elementColor: 'text-red-500' },
  'Touro': { elemento: 'Terra', regente: 'Vênus', elementColor: 'text-amber-600' },
  'Gêmeos': { elemento: 'Ar', regente: 'Mercúrio', elementColor: 'text-sky-500' },
  'Câncer': { elemento: 'Água', regente: 'Lua', elementColor: 'text-blue-500' },
  'Leão': { elemento: 'Fogo', regente: 'Sol', elementColor: 'text-red-500' },
  'Virgem': { elemento: 'Terra', regente: 'Mercúrio', elementColor: 'text-amber-600' },
  'Libra': { elemento: 'Ar', regente: 'Vênus', elementColor: 'text-sky-500' },
  'Escorpião': { elemento: 'Água', regente: 'Plutão', elementColor: 'text-blue-500' },
  'Sagitário': { elemento: 'Fogo', regente: 'Júpiter', elementColor: 'text-red-500' },
  'Capricórnio': { elemento: 'Terra', regente: 'Saturno', elementColor: 'text-amber-600' },
  'Aquário': { elemento: 'Ar', regente: 'Urano', elementColor: 'text-sky-500' },
  'Peixes': { elemento: 'Água', regente: 'Netuno', elementColor: 'text-blue-500' },
};

const SkeletonSigno = () => (
  <div className="bg-white rounded-2xl shadow-lg p-5 border border-purple-100">
    <div className="animate-pulse flex flex-col gap-3">
      <div className="h-4 w-20 bg-purple-100 rounded"></div>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-purple-100 rounded-full"></div>
        <div className="h-6 w-24 bg-purple-100 rounded"></div>
      </div>
      <div className="space-y-2">
        <div className="h-4 w-full bg-purple-100 rounded"></div>
        <div className="h-4 w-3/4 bg-purple-100 rounded"></div>
      </div>
    </div>
  </div>
);

const SignosSection = ({ loadingAnalysis, analysis, vertical }) => {
  const [selectedSign, setSelectedSign] = React.useState(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  if (loadingAnalysis) {
    const gridClass = vertical
      ? 'flex flex-col gap-4'
      : 'grid grid-cols-1 md:grid-cols-3 gap-6 mb-8';
    return (
      <div className={gridClass}>
        {[1, 2, 3].map((i) => (
          <SkeletonSigno key={i} />
        ))}
      </div>
    );
  }

  if (!analysis?.Relatorio?.Signos) return null;

  const { SignoSolar, SignoLunar, Ascendente } = analysis.Relatorio.Signos;
  const signos = [SignoSolar, SignoLunar, Ascendente];

  const gridClass = vertical
    ? 'flex flex-col gap-4'
    : 'grid grid-cols-1 md:grid-cols-3 gap-6 mb-8';

  return (
    <>
      {vertical && (
        <h2 className="font-serif text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
          Sol, Lua e Ascendente
        </h2>
      )}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={gridClass}
      >
        {signos.map((signo, index) => {
          const info = signoInfo[signo.Signo];
          return (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg p-5 border border-purple-100 cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => {
                setSelectedSign(signo.Signo);
                setIsModalOpen(true);
              }}
            >
              <span className="text-xs font-medium uppercase tracking-wider text-purple-400">
                {signoLabels[index]}
              </span>
              <div className="flex items-center gap-3 mt-1 mb-3">
                <div className="text-3xl">{signo.Simbolo}</div>
                <div>
                  <h3 className="font-serif text-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {signo.Signo}
                  </h3>
                  {info && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 font-medium">
                        {info.elemento}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-gray-500 font-medium">
                        ♦ {info.regente}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <p className="font-body text-gray-600 text-sm leading-relaxed line-clamp-3">{signo.Interpretacao}</p>
            </div>
          );
        })}
      </motion.div>
      <ZodiacSignModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        sign={selectedSign}
      />
    </>
  );
};

export default SignosSection;
